import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { apiGetMatches, apiGetEvents, apiGetLeaderboard } from "../api";
import { useAuth } from "../auth/AuthProvider";
import Spinner, { SkeletonCard } from "../components/Spinner";
import LongTermBetsBanner from "../components/LongTermBetsBanner";
import { formatDateRange, formatMatchDate, formatMatchTime, isToday, getRelativeDayLabel } from "../utils/date";
import { formatNumber } from "../utils/format";
import { supabase, isSupabaseConfigured } from "../lib/supabase";
import { CURRENT_TOURNAMENT } from "../config/tournament";

const MATCH_DURATION_MS = 4.5 * 60 * 60 * 1000; // 4.5 hours from match start

const STATUS_BADGE = {
  UPCOMING: "bg-blue-900/50 text-blue-400 border-blue-800",
  LIVE: "bg-emerald-900/50 text-emerald-400 border-emerald-800",
  COMPLETED: "bg-gray-800/80 text-gray-400 border-gray-700",
  ABANDONED: "bg-red-900/50 text-red-400 border-red-800",
  NO_RESULT: "bg-gray-800/80 text-gray-400 border-gray-700",
};

export default function Home() {
  const [matches, setMatches] = useState([]);
  const [event, setEvent] = useState(null);
  const [lockTimes, setLockTimes] = useState({});
  const [userBetMatchIds, setUserBetMatchIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [topPlayers, setTopPlayers] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([apiGetMatches(), apiGetEvents()])
      .then(([m, events]) => {
        setMatches(m);
        if (events?.length) setEvent(events[0]);
        // Build lock times from match data (already fetched)
        const lockMap = {};
        m.forEach((match) => { if (match.lockTime) lockMap[match.matchId] = match.lockTime; });
        setLockTimes(lockMap);

        // If tournament is complete, fetch leaderboard for top 3
        const allComplete = m.length > 0 && m.every((match) => match.status === "COMPLETED");
        if (allComplete) {
          apiGetLeaderboard("global", null, { page: 1, pageSize: 3 })
            .then((result) => { if (result.data?.length) setTopPlayers(result.data); })
            .catch(() => {});
        }
      })
      .finally(() => setLoading(false));
  }, []);

  // Fetch user's placed bets (just match IDs)
  const fetchUserBets = React.useCallback(() => {
    if (!user?.userId || !supabase || !isSupabaseConfigured()) {
      setUserBetMatchIds(new Set());
      return;
    }
    supabase
      .from("bets")
      .select("match_id")
      .eq("user_id", user.userId)
      .then(({ data }) => {
        if (data) {
          setUserBetMatchIds(new Set(data.map((r) => r.match_id)));
        }
      });
  }, [user?.userId]);

  useEffect(() => { fetchUserBets(); }, [fetchUserBets]);

  // Refetch when user navigates back (e.g. after placing a bet)
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === "visible") fetchUserBets(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchUserBets]);

  // Derive effective status: lock_time passed + within 4.5hrs → LIVE, past 4.5hrs → COMPLETED
  const effectiveMatches = useMemo(() => {
    const now = new Date();
    return matches.map((m) => {
      const lt = lockTimes[m.matchId];
      const lockPassed = lt && new Date(lt) <= now;

      if (m.status === "COMPLETED") return m;

      if (lockPassed) {
        const estimatedEnd = new Date(lt).getTime() + MATCH_DURATION_MS;
        if (now.getTime() < estimatedEnd) {
          return { ...m, status: "LIVE" };
        }
        return { ...m, status: "COMPLETED" };
      }
      return m;
    });
  }, [matches, lockTimes]);

  // Get next 3 upcoming or live matches for preview
  const upcomingMatches = effectiveMatches
    .filter((m) => m.status === "UPCOMING" || m.status === "LIVE")
    .slice(0, 3);

  const isTournamentComplete = effectiveMatches.length > 0 && effectiveMatches.every((m) => m.status === "COMPLETED");

  // Fire confetti once when the tournament-complete view first renders
  useEffect(() => {
    if (!isTournamentComplete) return;
    const key = `confettiFired:${CURRENT_TOURNAMENT.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, "1");
    const duration = 4000;
    const end = Date.now() + duration;
    const colors = ["#fbbf24", "#f59e0b", "#fcd34d", "#fde68a", "#fef3c7"];
    (function frame() {
      confetti({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0, y: 0.7 }, colors });
      confetti({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1, y: 0.7 }, colors });
      if (Date.now() < end) requestAnimationFrame(frame);
    })();
    // One big burst from the centre at the start
    confetti({ particleCount: 150, spread: 100, origin: { y: 0.5 }, colors });
  }, [isTournamentComplete]);

  const tournamentDateRange = event
    ? formatDateRange(event.startDate, event.endDate)
    : CURRENT_TOURNAMENT.fallbackDateRange;

  const PODIUM_STYLE = {
    1: { badge: "bg-gradient-to-br from-yellow-400 to-amber-600 text-gray-900", ring: "ring-2 ring-yellow-500/40", label: "\uD83E\uDD47" },
    2: { badge: "bg-gradient-to-br from-gray-300 to-gray-500 text-gray-900", ring: "ring-2 ring-gray-400/30", label: "\uD83E\uDD48" },
    3: { badge: "bg-gradient-to-br from-amber-600 to-amber-800 text-amber-100", ring: "ring-2 ring-amber-600/30", label: "\uD83E\uDD49" },
  };

  // ── Tournament Complete view ────────────────────────────────────────────
  if (!loading && isTournamentComplete) {
    const champion = topPlayers[0];
    return (
      <div className="max-w-5xl mx-auto px-4 py-10">
        {/* Hero */}
        <div className="text-center mb-10 animate-fade-in">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-3">
            <span className="bg-gradient-to-r from-yellow-300 via-amber-400 to-orange-500 bg-clip-text text-transparent">
              Super Selector
            </span>
          </h1>
          <p className="text-amber-400 font-semibold text-lg tracking-wide">
            Tournament Complete
          </p>
        </div>

        {/* Champion Card */}
        {champion && (
          <div className="card mb-8 animate-slide-up text-center bg-gradient-to-br from-yellow-900/30 via-gray-900 to-amber-900/20 border-yellow-600/30">
            <div className="text-5xl mb-3">{"\uD83C\uDFC6"}</div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-yellow-300 mb-2">
              {champion.displayName} wins SuperSelector
            </h2>
            <p className="text-gray-400 text-sm mb-4">
              {CURRENT_TOURNAMENT.name}
            </p>
            <p className="text-4xl sm:text-5xl font-extrabold text-yellow-400">
              {formatNumber(champion.totalScore ?? champion.score)}
            </p>
            <p className="text-gray-500 text-sm mt-1">points</p>
          </div>
        )}

        {/* Top 3 Podium */}
        {topPlayers.length >= 3 && (
          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-8 animate-slide-up" style={{ animationDelay: "100ms" }}>
            {[topPlayers[1], topPlayers[0], topPlayers[2]].map((e, i) => {
              const rank = [2, 1, 3][i];
              const style = PODIUM_STYLE[rank];
              const isFirst = rank === 1;
              return (
                <div
                  key={e.userId}
                  className={`card text-center px-2 sm:px-4 ${style.ring} ${isFirst ? "sm:-mt-4" : ""}`}
                >
                  <div className="text-xl sm:text-3xl mb-2">{style.label}</div>
                  <div className={`inline-block px-2 sm:px-3 py-1 rounded-full text-xs font-bold mb-2 ${style.badge}`}>#{rank}</div>
                  <h3 className={`font-bold truncate ${isFirst ? "text-sm sm:text-lg text-yellow-300" : "text-xs sm:text-sm text-gray-200"}`}>{e.displayName}</h3>
                  <p className={`font-extrabold mt-1 ${isFirst ? "text-2xl sm:text-3xl text-yellow-400" : "text-lg sm:text-xl text-gray-300"}`}>{formatNumber(e.totalScore ?? e.score)}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">points</p>
                </div>
              );
            })}
          </div>
        )}

        {/* Tournament Info */}
        <div className="card mb-8 text-center animate-slide-up" style={{ animationDelay: "200ms" }}>
          <p className="text-lg font-bold text-emerald-300">
            {"\uD83C\uDFCF"} {CURRENT_TOURNAMENT.name} — Closed
          </p>
          <p className="text-gray-500 text-sm mt-1">{effectiveMatches.length} matches played</p>
          <p className="text-gray-400 text-sm mt-3">
            All bets settled, leaderboard final. See you at the next tournament!
          </p>
        </div>

        {/* CTA */}
        <div className="text-center animate-slide-up" style={{ animationDelay: "300ms" }}>
          <Link
            to="/leaderboard"
            className="btn-primary text-lg px-10 py-4 inline-flex items-center gap-2"
          >
            View Final Leaderboard
            <span>{"\u2192"}</span>
          </Link>
        </div>

        {/* Secondary CTAs */}
        <div className="grid sm:grid-cols-2 gap-4 mt-10 animate-slide-up" style={{ animationDelay: "400ms" }}>
          <Link
            to="/long-term-bets"
            className="card hover:border-purple-600 hover:shadow-lg hover:shadow-purple-900/20 transition-all duration-300 group text-center py-6"
          >
            <div className="text-3xl mb-2">{"\uD83C\uDFC6"}</div>
            <h3 className="font-semibold text-purple-300 group-hover:text-purple-200">Tournament Predictions</h3>
            <p className="text-gray-500 text-sm mt-1">View your prediction results</p>
          </Link>
          <Link
            to="/play"
            className="card hover:border-brand-600 hover:shadow-lg hover:shadow-brand-900/20 transition-all duration-300 group text-center py-6"
          >
            <div className="text-3xl mb-2">{"\uD83D\uDCCA"}</div>
            <h3 className="font-semibold text-brand-300 group-hover:text-brand-200">Browse Matches</h3>
            <p className="text-gray-500 text-sm mt-1">Revisit all match results</p>
          </Link>
        </div>
      </div>
    );
  }

  // ── Active tournament view (existing) ───────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      {/* Hero */}
      <div className="text-center mb-12 animate-fade-in">
        <h1 className="text-4xl sm:text-5xl font-extrabold mb-3">
          <span className="bg-gradient-to-r from-brand-300 via-brand-400 to-emerald-400 bg-clip-text text-transparent">
            Super Selector
          </span>
        </h1>
        <p className="text-brand-400 font-semibold text-lg tracking-wide mb-4">
          Embrace the Chaos.
        </p>
        <div className="text-gray-400 text-sm max-w-lg mx-auto space-y-3 text-left">
          <p>
            Pick winners. Guess totals. Choose players. Slot them for multipliers that can <span className="text-white font-medium">explode your score</span>.
          </p>
          <p>
            Down by 200 points? One perfect round can erase that. Multipliers get wilder as the tournament heats up. A single bold pick on a big night can launch you from last to first. A super over call pays <span className="text-brand-300 font-bold">5x</span>. A gutsy side bet that everyone else dodged? That's <span className="text-white font-medium">your moment</span>.
          </p>
          <p className="text-white font-semibold">
            The bigger the chaos, the bigger your opportunity.
          </p>
          <p>
            Leaders fall. Underdogs rise. The leaderboard is a living thing — and the final match matters more than the first ten combined.
          </p>
          <p className="text-brand-300 font-bold tracking-wide">
            Be bold. Pick. Win.
          </p>
        </div>

        {/* Primary CTA */}
        <div className="mt-8">
          <Link
            to="/play"
            className="btn-primary text-lg px-10 py-4 inline-flex items-center gap-2"
          >
            Start Playing
            <span>{"\u2192"}</span>
          </Link>
        </div>

        {!user && (
          <p className="text-gray-500 mt-4 text-sm">Log in from the navbar to start placing bets.</p>
        )}
      </div>

      <LongTermBetsBanner />

      {/* Tournament summary */}
      {event && (
        <div className="card mb-10 animate-slide-up">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-lg font-bold text-gray-200">{event.name}</h2>
              <p className="text-gray-400 text-sm mt-1">{tournamentDateRange}</p>
            </div>
            <span className="px-3 py-1 rounded-full text-xs border bg-brand-900/50 text-brand-300 border-brand-800">
              {event.status}
            </span>
          </div>
        </div>
      )}

      {/* Quick Preview of Upcoming Matches */}
      {loading ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : upcomingMatches.length > 0 ? (
        <div className="mb-10 animate-slide-up" style={{ animationDelay: "100ms" }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-300">Next Matches</h2>
            <Link to="/play" className="text-brand-400 hover:text-brand-300 text-sm">
              View all {"\u2192"}
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingMatches.map((m, i) => (
              <MatchCard key={m.matchId} match={m} delay={i * 60} userHasBet={userBetMatchIds.has(m.matchId)} />
            ))}
          </div>
          <p className="text-gray-500 text-xs mt-4 text-center">
            Click a match to place your bets
          </p>
        </div>
      ) : (
        <div className="card text-center py-12 animate-slide-up">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-800/50 flex items-center justify-center">
            <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-gray-400 text-lg mb-2">No upcoming matches</p>
          <p className="text-gray-600 text-sm">Check back soon for fixtures.</p>
        </div>
      )}

      {/* Secondary CTAs */}
      <div className="grid sm:grid-cols-2 gap-4 animate-slide-up" style={{ animationDelay: "200ms" }}>
        <Link
          to="/long-term-bets"
          className="card hover:border-purple-600 hover:shadow-lg hover:shadow-purple-900/20 transition-all duration-300 group text-center py-6"
        >
          <div className="text-3xl mb-2">{"\uD83C\uDFC6"}</div>
          <h3 className="font-semibold text-purple-300 group-hover:text-purple-200">Tournament Predictions</h3>
          <p className="text-gray-500 text-sm mt-1">Predict winners, top scorers & more</p>
        </Link>
        <Link
          to="/leaderboard"
          className="card hover:border-brand-600 hover:shadow-lg hover:shadow-brand-900/20 transition-all duration-300 group text-center py-6"
        >
          <div className="text-3xl mb-2">{"\uD83D\uDCCA"}</div>
          <h3 className="font-semibold text-brand-300 group-hover:text-brand-200">Leaderboard</h3>
          <p className="text-gray-500 text-sm mt-1">See where you stand</p>
        </Link>
      </div>
    </div>
  );
}

function MatchCard({ match: m, delay = 0, userHasBet = false }) {
  const navigate = useNavigate();
  const statusLabel = m.status.charAt(0) + m.status.slice(1).toLowerCase();
  const isLive = m.status === "LIVE";
  const isUpcoming = m.status === "UPCOMING";
  const dayLabel = getRelativeDayLabel(m.scheduledTime);

  return (
    <div
      onClick={() => navigate(`/match/${m.matchId}`)}
      className={`card hover:border-brand-600 hover:shadow-lg hover:shadow-brand-900/20 transition-all duration-300 cursor-pointer group animate-slide-up ${
        userHasBet ? "border-l-2 border-l-emerald-600" : ""
      }`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className="font-bold text-gray-200 group-hover:text-white transition-colors">{m.teamA}</span>
          <span className="text-gray-600 text-xs">vs</span>
          <span className="font-bold text-gray-200 group-hover:text-white transition-colors">{m.teamB}</span>
        </div>
        <div className="flex items-center gap-2">
          {userHasBet && (isUpcoming || isLive) && (
            <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-900/30 text-emerald-400 border border-emerald-800/50">
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
              Bet placed
            </span>
          )}
          <span className={`text-xs px-2.5 py-1 rounded-full border ${STATUS_BADGE[m.status] || ""}`}>
            {isLive && (
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5 align-middle" />
            )}
            {statusLabel}
          </span>
        </div>
      </div>
      <div className="text-xs text-gray-500">
        <div>{m.venue}</div>
        <div className="mt-1">
          <span className="text-gray-400">{dayLabel}</span> at {formatMatchTime(m.scheduledTime)}
        </div>
      </div>
      {m.result && <div className="text-xs text-emerald-400 mt-2">{m.result}</div>}
      {(isUpcoming || isLive) && !m.result && (
        <button className={`w-full mt-3 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
          userHasBet
            ? "bg-emerald-900/20 border border-emerald-800/50 text-emerald-400 group-hover:bg-emerald-900/30 group-hover:border-emerald-700"
            : "bg-brand-600/20 border border-brand-700/50 text-brand-300 group-hover:bg-brand-600/30 group-hover:border-brand-600"
        }`}>
          {userHasBet ? "Update Bets →" : "Place Bets →"}
        </button>
      )}
    </div>
  );
}
